/**
 * Settles with `promise`, or with `fallback` once `ms` milliseconds pass without it settling.
 *
 * For awaits that sit on a user action and must not hang on a third party that may never
 * answer. A rejection that lands before the deadline still rejects.
 *
 * @param {Promise<T> | T} promise - The value to wait for.
 * @param {number} ms - How long to wait before giving up.
 * @param {F} fallback - The value to settle with on timeout.
 * @returns {Promise<T | F>} Whichever settles first.
 */
export const withTimeout = async <T, F>(promise: Promise<T> | T, ms: number, fallback: F): Promise<T | F> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<F>((resolve) => {
    timeoutId = setTimeout(() => resolve(fallback), ms)
  })
  try {
    return await Promise.race([promise, timeout])
  } finally {
    clearTimeout(timeoutId)
  }
}
